import { useState, type ReactNode } from "react";
import { cargaDecision } from "./decision-modelo.ts";
import { Check, CircleHelp, MessageSquarePlus, ThumbsDown, TriangleAlert, X, type LucideIcon } from "lucide-react";

import type { Factura, Movimiento } from "../api/tipos.ts";
import { fechaConHora, haceRato, pesos } from "../formato.ts";
import { ORIGEN } from "../vocabulario.ts";
import { usarClientes, type Decision } from "../ganchos/datos.ts";
import { ejesDe, ladosDe, resumenDeCoincidencias, type Cierre, type Eje } from "./ejes.ts";
import { CampoFacturas } from "./CampoFacturas.tsx";
import { identidadDe } from "./identidad-desde-factura.ts";
import { CampoCliente } from "./CampoCliente.tsx";
import type { ClienteElegido } from "./cliente-modelo.ts";
import { notaSinFactura, RAZONES_SIN_FACTURA, viaDeFactura, type RazonSinFactura } from "./atajo-de-pago.ts";
import { Aviso, Boton, Tarjeta, cx } from "./ui.tsx";
import { CuentaDelPago } from "./CuentaDelPago.tsx";

/**
 * Un encuentro entre dos lados, que es lo que de verdad es una decisión.
 *
 * Sustituye a la tarjeta con insignias. La diferencia no es estética: antes el
 * veredicto iba arriba y la evidencia debajo, comprimida en una tira de
 * etiquetas. Aquí la evidencia ES la estructura — el banco a un lado, tu
 * registro al otro, y entre los dos los ejes en los que coinciden o no.
 *
 * Tres reglas de color que vienen de la identidad y que no se relajan aquí:
 * el azul es siempre el lado del banco, el verde siempre el tuyo, y el teal
 * aparece SOLO donde los dos se tocaron. Si el teal se usa para decorar, deja
 * de significar.
 */
export function Encuentro({
  movimiento,
  cartera = [],
  decidiendo,
  alDecidir,
  inicio,
  plana,
}: {
  movimiento: Movimiento;
  cartera?: Factura[];
  decidiendo?: boolean;
  alDecidir: (d: Decision) => void;
  /** Con qué abrir las acciones cuando se llega desde el menú de una fila. */
  inicio?: "nota" | "sin-factura";
  /** Sin marco de tarjeta: va dentro de una fila de la bandeja, que ya lo tiene. */
  plana?: boolean;
}) {
  const m = movimiento;

  const { banco, registro } = ladosDe(m);
  const ejes = ejesDe(m);
  const resumen = resumenDeCoincidencias(ejes);

  const alarma = m.origen === "humano-obsoleto" || m.origen === "clave-ambigua";

  return (
    <Tarjeta className={cx(plana ? "overflow-visible rounded-none border-0 shadow-none" : "subir overflow-hidden")}>
      {/* — lo que cambia el sentido de la decisión va ANTES que nada — */}
      {alarma && (
        <div className="p-5 pb-0">
          <Aviso
            tono={m.origen === "humano-obsoleto" ? "peligro" : "riesgo"}
            titulo={ORIGEN[m.origen].texto}
          >
            {m.origen === "humano-obsoleto" && m.decisionPrevia ? (
              <>
                {m.decisionPrevia.quien} lo confirmó {haceRato(m.decisionPrevia.cuando)} y los datos
                cambiaron después. Esa confirmación ya no vale.
              </>
            ) : (
              ORIGEN[m.origen].explica
            )}
          </Aviso>
        </div>
      )}

      {/* — el encuentro —
          Con registro al otro lado, las tres columnas son el producto: banco,
          ejes, y lo que esperabas. Sin él no hay encuentro que mostrar, y las
          tres columnas gastaban media pantalla en unos ejes vacíos y un párrafo
          genérico repetido en cada tarjeta. Una bandeja con veinte pagos era
          imposible de recorrer, así que ese caso se colapsa a una sola línea. */}
      {registro ? (
        <div className="@container">
          <div className="grid items-stretch @2xl:grid-cols-[1fr_auto_1fr]">
            <Lado
              rotulo="El banco dice"
              color="dato"
              monto={banco.montoCentavos}
              nombre={banco.quien || <SinNombre sucursal={banco.sucursal} />}
              pie={[fechaConHora(banco.fecha), banco.llave].filter(Boolean).join(" · ")}
            />
            <Ejes ejes={ejes} />
            <Lado
              rotulo="Tú esperabas"
              color="exito"
              monto={registro.montoCentavos}
              nombre={registro.cliente}
              pie={registro.factura ? `Factura ${registro.factura}` : ""}
              alineadoDerecha
            />
          </div>
        </div>
      ) : (
        // Teñida, y con menos aire vertical que el cuerpo: es una cabecera, no
        // una sección más. El tinte hace dos trabajos a la vez —separa el dato
        // del banco de la zona donde se decide, y le da a cada tarjeta un
        // borde superior visible— que la línea divisoria sola no hacía: sobre
        // papel a 98% y tarjeta a 100%, un pelo de borde se pierde en cuanto
        // hay veinte tarjetas seguidas.
        //
        // `items-center` y no `items-baseline`: con un rótulo de 12px junto a un
        // monto de 24px, la línea de base deja el rótulo visualmente hundido.
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-dato-suave/60 px-5 py-2">
          <span className="text-xs font-medium tracking-wide text-dato-tinta uppercase">
            El banco dice
          </span>
          <span className="text-2xl font-semibold tracking-tight text-tinta tabular-nums">
            {pesos(banco.montoCentavos)}
          </span>
          {banco.quien ? (
            <span className="text-sm font-medium text-tinta">{banco.quien}</span>
          ) : (
            <SinNombre sucursal={banco.sucursal} />
          )}
          <span className="text-sm text-tenue">
            {[fechaConHora(banco.fecha), banco.llave].filter(Boolean).join(" · ")}
          </span>
        </div>
      )}

      {/* — la razón, en palabras, y la acción — */}
      <div className="space-y-3 border-t border-borde p-5">
        {/* La razón es el texto que la persona SÍ lee entero antes de decidir,
            así que es el que más pierde con un renglón de 1200px. La tarjeta
            ocupa todo el ancho —los montos y las cifras lo aprovechan—; el
            párrafo no. */}
        <p className="max-w-prose text-sm leading-relaxed text-tenue">
          {registro && resumen && <span className="font-medium text-tinta">{resumen} </span>}
          {m.razon}
        </p>

        {/* A qué cuenta del negocio entró, y elegirla si no se sabe (SPEC-030 §6). */}
        <CuentaDelPago movimiento={m} />

        <AccionesDePago
          movimiento={m}
          registro={registro}
          cartera={cartera}
          decidiendo={decidiendo}
          alDecidir={alDecidir}
          inicio={inicio}
        />
      </div>
    </Tarjeta>
  );
}

/**
 * Las acciones sobre un pago: etiquetar, confirmar, descartar, anotar.
 *
 * Vive aparte porque las usan DOS pantallas —la bandeja y el detalle de un
 * movimiento— y duplicarlas garantizaba que se separaran. Que no se separen
 * importa más de lo normal aquí: son los botones que mueven plata en el libro,
 * y dos versiones que divergen es cómo se acaba teniendo un «No es» que en una
 * pantalla descarta y en la otra confirma.
 *
 * `aviso` lo pone quien la usa: en la bandeja no hay nada que advertir, y en el
 * detalle depende de si el pago ya lo resolvió alguien (ver `correccion.ts`).
 */
export function AccionesDePago({
  movimiento: m,
  registro,
  cartera = [],
  decidiendo,
  alDecidir,
  aviso,
  inicio,
}: {
  movimiento: Movimiento;
  registro: { cliente: string } | null;
  /** La cartera. Vacía = no hay dónde mirar, y no se marca nada (§10.1). */
  cartera?: Factura[];
  decidiendo?: boolean;
  alDecidir: (d: Decision) => void;
  aviso?: ReactNode;
  /** Con qué abrirse cuando se llega desde el menú de una fila. */
  inicio?: "nota" | "sin-factura";
}) {
  const [nota, setNota] = useState("");
  const [conNota, setConNota] = useState(inicio === "nota");
  const [sinFactura, setSinFactura] = useState(inicio === "sin-factura");
  // La misma regla que la fila (`atajo-de-pago.ts`): si hay una factura que
  // ligar, el «sí» la lleva o hay que elegirla. Dos reglas distintas —una en
  // la fila y otra al abrirla— era cómo se colaba el sí sin factura.
  const atajo = viaDeFactura(m);
  // A quién se le atribuye, elegido de la lista (SPEC-029 §5). Si las facturas
  // elegidas dicen de quién es, eso se propone solo; la X lo quita.
  const [elegido, setElegido] = useState<ClienteElegido | null>(null);
  const [sinSugerido, setSinSugerido] = useState(false);
  const clientes = usarClientes(!registro).data?.clientes ?? [];
  const [facturas, setFacturas] = useState<string[]>([]);

  // Quién es, según las facturas elegidas. El documento es la identidad y el
  // nombre una copia: elegirlas de la cartera evita que el nombre dependa de
  // que alguien lo teclee igual dos veces (SPEC-028 §4.2).
  const identidad = identidadDe(facturas, cartera);
  const sugerido: ClienteElegido | null =
    !sinSugerido && identidad?.cliente ? { nombre: identidad.cliente, nit: identidad.nit, nuevo: false } : null;
  const efectivo = elegido ?? sugerido;

  // La carga se arma en `decision-modelo.ts`, no aquí: un botón que dice «No es»
  // y manda CONFIRMAR se ve perfecto y resuelve el caso al revés, así que eso
  // tiene que poder probarse sin pintar nada (CA-11).
  const decidir = (
    accion: Decision["accion"],
    cliente?: string,
    extra: { facturas?: string[]; clienteNit?: string; nota?: string } = {},
  ) =>
    alDecidir(
      cargaDecision({
        clave: m.clave,
        accion,
        // Lo escrito a mano manda sobre lo rellenado: quien lo corrige sabe algo
        // que la factura no dice.
        cliente: cliente || identidad?.cliente,
        clienteNit: extra.clienteNit ?? identidad?.nit,
        facturas: extra.facturas ?? facturas,
        nota: extra.nota ?? nota,
      }),
    );

  // Sin factura escrita, ¿qué pide el sí? Con una escrita, manda lo escrito.
  const pendienteDeFactura = !facturas.length && (atajo.tipo === "sugerida" || atajo.tipo === "varias");

  return (
    <div className="space-y-3">
      {aviso}
      {conNota && (
        <input
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Nota: qué comprobaste, con quién hablaste…"
          className="h-10 w-full rounded-xl border border-borde bg-superficie px-3.5 text-sm placeholder:text-tenue-2 focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
        />
      )}

      {/* Contra qué se aplica. Va antes de los botones porque se decide con el
          pago delante, no después de haberlo confirmado. */}
      <CampoFacturas
        valor={facturas}
        alCambiar={setFacturas}
        cartera={cartera}
        montoCentavos={m.montoCentavos}
        deshabilitado={decidiendo}
      />

      {registro ? (
        <div className="space-y-3">
          {atajo.tipo === "varias" && !facturas.length && (
            <div className="space-y-1.5 text-xs">
              <p className="text-tenue">
                Hay {atajo.candidatas.length} facturas pendientes de {registro.cliente} que podrían
                ser la de este pago. Elige una:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {atajo.candidatas.map((f) => (
                  <button
                    key={f.numero}
                    type="button"
                    disabled={decidiendo}
                    onClick={() => setFacturas([f.numero])}
                    className="rounded-lg border border-borde bg-superficie px-2 py-1 font-medium hover:border-marca"
                  >
                    <span className="cifra">{f.numero}</span>
                    <span className="cifra font-normal opacity-80"> · {pesos(f.totalCentavos)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {facturas.length ? (
              <Boton variante="primario" icono={Check} cargando={decidiendo} onClick={() => decidir("CONFIRMAR")}>
                Sí, es de {registro.cliente} · {facturas.join(", ")}
              </Boton>
            ) : atajo.tipo === "sugerida" ? (
              <Boton
                variante="primario"
                icono={Check}
                cargando={decidiendo}
                onClick={() => decidir("CONFIRMAR", undefined, { facturas: [atajo.factura.numero] })}
              >
                Sí, con {atajo.factura.numero}
              </Boton>
            ) : atajo.tipo === "varias" ? null : (
              <Boton variante="primario" icono={Check} cargando={decidiendo} onClick={() => decidir("CONFIRMAR")}>
                Sí, es de {registro.cliente}
                {atajo.tipo === "trae" ? ` · ${atajo.factura}` : ""}
              </Boton>
            )}
            {pendienteDeFactura && (
              <Boton
                variante={sinFactura ? "suave" : "fantasma"}
                disabled={decidiendo}
                onClick={() => setSinFactura((v) => !v)}
              >
                Sin factura…
              </Boton>
            )}
            <Boton
              variante="peligro"
              icono={ThumbsDown}
              disabled={decidiendo}
              onClick={() => decidir("RECHAZAR")}
            >
              No es de este cliente
            </Boton>
            <BotonNota activo={conNota} alAlternar={() => setConNota((v) => !v)} />
          </div>
          {pendienteDeFactura && sinFactura && (
            <ConfirmarSinFactura
              motivo={
                atajo.tipo === "sugerida"
                  ? `Hay una factura pendiente de ${registro.cliente} por ${pesos(atajo.factura.totalCentavos)} (${atajo.factura.numero}).`
                  : `Hay ${atajo.tipo === "varias" ? atajo.candidatas.length : ""} facturas pendientes de ${registro.cliente} que podrían ser.`
              }
              decidiendo={decidiendo}
              alConfirmar={(n) => decidir("CONFIRMAR", undefined, { facturas: [], nota: [n, nota.trim()].filter(Boolean).join(" · ") })}
            />
          )}
        </div>
      ) : (
        <div className="space-y-3">
        {/* Una factura de la cartera propuso por sí sola (SPEC-028 §9): se acepta
            de un clic, con su documento, en vez de teclear el nombre. */}
        {atajo.tipo === "trae" && atajo.desde === "cartera" && !facturas.length && !elegido && (
          <div className="flex flex-wrap gap-2">
            <Boton
              variante="primario"
              icono={Check}
              cargando={decidiendo}
              onClick={() =>
                decidir("ETIQUETAR", m.cliente ?? "", { facturas: [atajo.factura], clienteNit: m.clienteNit ?? "" })
              }
            >
              Sí, es de {m.cliente} · {atajo.factura}
            </Boton>
            <span className="self-center text-xs text-tenue">o dinos de quién es:</span>
          </div>
        )}
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (efectivo) decidir("ETIQUETAR", efectivo.nombre, { clienteNit: efectivo.nit });
          }}
        >
          <CampoCliente
            clientes={clientes}
            elegido={efectivo}
            origen={!elegido && sugerido ? "según la factura" : undefined}
            alElegir={(c) => {
              setElegido(c);
              if (!c) setSinSugerido(true);
            }}
            deshabilitado={decidiendo}
          />
          <Boton type="submit" variante="primario" icono={Check} cargando={decidiendo} disabled={!efectivo}>
            Es de este cliente
          </Boton>
          <Boton
            type="button"
            variante="suave"
            icono={X}
            disabled={decidiendo}
            onClick={() => decidir("DESCARTAR")}
          >
            No es un pago nuestro
          </Boton>
          <BotonNota activo={conNota} alAlternar={() => setConNota((v) => !v)} />
        </form>
        </div>
      )}
    </div>
  );
}

/**
 * Confirmar sin factura habiendo una que encaja: cuesta un paso y una razón.
 *
 * No se prohíbe —hay anticipos y pagos a varias facturas—, pero se hace a
 * propósito y deja rastro: la razón viaja en la nota, con quién y cuándo. Si
 * alguien confirma así a menudo, se verá.
 */
function ConfirmarSinFactura({
  motivo,
  decidiendo,
  alConfirmar,
}: {
  motivo: string;
  decidiendo?: boolean;
  alConfirmar: (nota: string) => void;
}) {
  const [razon, setRazon] = useState<RazonSinFactura | null>(null);
  const [texto, setTexto] = useState("");
  const [error, setError] = useState("");
  const obligatorio = razon === "no-aparece";

  return (
    <div className="space-y-2.5 rounded-xl border border-borde bg-superficie-2 p-3.5 text-sm">
      <p className="font-medium text-tinta">Confirmar sin factura</p>
      <p className="text-tenue">{motivo} ¿Por qué no se liga?</p>
      <div className="flex flex-wrap gap-1.5">
        {RAZONES_SIN_FACTURA.map((r) => (
          <button
            key={r.clave}
            type="button"
            onClick={() => {
              setRazon(r.clave);
              setError("");
            }}
            className={cx(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              razon === r.clave
                ? "border-marca bg-marca-suave text-marca-tinta"
                : "border-borde bg-superficie text-tenue hover:border-borde-fuerte",
            )}
          >
            {r.texto}
          </button>
        ))}
      </div>
      <input
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          setError("");
        }}
        placeholder={obligatorio ? "Qué factura hay que buscar (obligatorio)" : "Detalle (opcional)"}
        className="h-9 w-full rounded-lg border border-borde bg-superficie px-3 text-sm placeholder:text-tenue-2 focus:border-marca focus:outline-none"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Boton
          variante="suave"
          cargando={decidiendo}
          onClick={() => {
            if (!razon) return setError("Elige por qué no se liga la factura.");
            const n = notaSinFactura(razon, texto);
            if (!n) return setError("Escribe qué factura hay que buscar.");
            alConfirmar(n);
          }}
        >
          Confirmar sin factura
        </Boton>
        {error && <span className="text-xs text-riesgo-tinta">{error}</span>}
      </div>
    </div>
  );
}

/** Un lado del encuentro. El color dice de quién es el dato, siempre igual. */
export function Lado({
  rotulo,
  color,
  monto,
  nombre,
  pie,
  alineadoDerecha,
}: {
  rotulo: string;
  color: "dato" | "exito";
  monto: number | null;
  /** Nodo y no cadena: cuando el banco no da nombre, aquí va de dónde vino. */
  nombre: React.ReactNode;
  pie: string;
  alineadoDerecha?: boolean;
}) {
  return (
    <div
      className={cx(
        // Cada lado con SU color, no un tinte único para toda la franja: teñir
        // las dos mitades de azul pintaría «Tú esperabas» con el color del
        // banco, y en esta pantalla el color ya significa algo. Los ejes del
        // medio se quedan sobre el papel de la tarjeta: son el punto de
        // encuentro, no un tercer lado.
        "min-w-0 px-5 py-4",
        color === "dato" ? "bg-dato-suave/60" : "bg-exito-suave/60",
        alineadoDerecha && "@2xl:text-right",
      )}
    >
      <div
        className={cx(
          "flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase",
          color === "dato" ? "text-dato-tinta" : "text-exito-tinta",
          alineadoDerecha && "@2xl:flex-row-reverse",
        )}
      >
        <span
          className={cx("size-2 rounded-[3px]", color === "dato" ? "bg-dato" : "bg-exito")}
          aria-hidden
        />
        {rotulo}
      </div>
      <p className="cifra-titular mt-2.5 text-3xl">{pesos(monto)}</p>
      {/* `truncate` solo cuando es texto: sobre el bloque de «sin nombre»
          recortaría la segunda línea, que es justo la que informa. */}
      <p className={cx("mt-2 text-sm font-medium text-tinta", typeof nombre === "string" && "truncate")}>
        {nombre}
      </p>
      {pie && <p className="mt-0.5 truncate text-sm text-tenue">{pie}</p>}
    </div>
  );
}

/**
 * El medio: por qué se unieron los dos lados.
 *
 * Una lista corta, no un dibujo. La versión anterior eran líneas entre las dos
 * columnas que cerraban en teal o se rompían en ámbar; era bonita y nadie la
 * entendía sin que se la explicaran (30-sep-2026): todo dependía del color, el
 * detalle solo salía al pasar el ratón, y no decía para qué estaba ahí.
 *
 * Ahora cada dato lleva icono, color Y palabras, y la pregunta que responde va
 * encima. Se lee sin leyenda, en el celular y sin distinguir colores.
 */
export function Ejes({ ejes, vacio }: { ejes: Eje[]; vacio?: boolean }) {
  if (vacio) return <MedioVacio>nada al otro lado</MedioVacio>;

  if (!ejes.length) return <div className="border-y border-borde @2xl:w-44 @2xl:border-x @2xl:border-y-0" />;

  return (
    <div className="flex flex-col justify-center gap-2.5 border-y border-borde bg-superficie-2/60 px-5 py-4 @2xl:w-64 @2xl:border-x @2xl:border-y-0">
      <p className="text-xs font-semibold text-tinta">¿Por qué creemos que es este pago?</p>
      <ul className="space-y-1.5">
        {ejes.map((e) => (
          <Coincidencia key={e.clave} eje={e} />
        ))}
      </ul>
    </div>
  );
}

const ICONO: Record<Cierre, LucideIcon> = {
  cierra: Check,
  hueco: TriangleAlert,
  ausente: CircleHelp,
};

const TONO: Record<Cierre, { icono: string; detalle: string }> = {
  cierra: { icono: "text-marca", detalle: "text-tenue" },
  // Lo que no cuadra es lo que hay que mirar: es lo único con peso en el texto.
  hueco: { icono: "text-aviso", detalle: "font-medium text-aviso-tinta" },
  // Gris, nunca ámbar: «nadie lo comprobó» no es «no cuadra».
  ausente: { icono: "text-tenue-2", detalle: "text-tenue-2" },
};

/** Lo que dice el icono, para quien no lo ve. */
const LEIDO: Record<Cierre, string> = {
  cierra: "coincide",
  hueco: "no coincide del todo",
  ausente: "sin comprobar",
};

function Coincidencia({ eje }: { eje: Eje }) {
  const Icono = ICONO[eje.cierre];
  return (
    <li className="flex items-baseline gap-2 text-xs">
      <Icono className={cx("size-3.5 shrink-0 translate-y-0.5", TONO[eje.cierre].icono)} aria-hidden />
      <span className="sr-only">{LEIDO[eje.cierre]}:</span>
      <span className="w-14 shrink-0 font-semibold text-tinta">{eje.etiqueta}</span>
      <span className={cx("min-w-0", eje.cierre === "hueco" && "cifra", TONO[eje.cierre].detalle)}>
        {eje.detalle}
      </span>
    </li>
  );
}

/**
 * El medio cuando no hay nada que cruzar.
 *
 * Punteado y en gris, nunca en ámbar: una ausencia no es un fallo. «No cuadra»
 * y «nadie lo ha comprobado» son acusaciones distintas y la interfaz no puede
 * confundirlas.
 */
export function MedioVacio({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2.5 border-y border-borde bg-superficie-2/60 px-6 py-5 @2xl:w-44 @2xl:border-x @2xl:border-y-0">
      <span className="w-full border-t-2 border-dashed border-borde-fuerte" aria-hidden />
      <span className="text-center text-xs leading-snug text-balance text-tenue-2">{children}</span>
      <span className="w-full border-t-2 border-dashed border-borde-fuerte" aria-hidden />
    </div>
  );
}

function BotonNota({ activo, alAlternar }: { activo: boolean; alAlternar: () => void }) {
  return (
    <Boton
      type="button"
      variante={activo ? "suave" : "fantasma"}
      icono={MessageSquarePlus}
      onClick={alAlternar}
    >
      {activo ? "Quitar nota" : "Añadir nota"}
    </Boton>
  );
}

/**
 * Un pago que no dice de quién es.
 *
 * Una consignación en corresponsal entra sin contraparte, y esa decisión es
 * correcta: el banco dice en qué tienda pagaron, no quién. Meter la tienda donde
 * va la persona haría que etiquetar una consignación atribuyera todas las demás
 * de esa tienda al mismo cliente — el fallo de la llave del QR otra vez.
 *
 * Por eso la sucursal va DEBAJO y en gris: es de dónde vino, no quién pagó, y la
 * tarjeta tiene que decir las dos cosas sin confundirlas. La tabla de
 * Movimientos ya lo resolvía así; esto lo trae a la bandeja, que es donde el
 * pago espera una decisión.
 */
function SinNombre({ sucursal }: { sucursal?: string }) {
  return (
    <span className="block">
      <span className="text-sm text-tenue-2 italic">Sin nombre</span>
      {sucursal && <span className="block text-sm font-normal text-tenue">{sucursal}</span>}
    </span>
  );
}